import React, { memo, useState } from 'react';
import { Alert, Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import WorkflowAbandonDialog from './WorkflowAbandonDialog';
import WorkflowRetryDialog from './WorkflowRetryDialog';
import { useWorkflowWrite } from '../hooks/useWorkflowWrite';
import { hasNoSessionBehindIt, isRunActive, statusChip } from '../runStatus';
import { safeFormatAge, safeFormatDate } from '../../../shared/utils/dateUtils';
import type { WorkflowRunSummary } from '../../../types/backend';

interface Props {
  run: WorkflowRunSummary;
  selected: boolean;
  onSelect: (runId: string) => void;
  /** Retry the step this run is blocked on, and stop the run. Omitted, the row only reads. */
  onRetryStep?: (runId: string, nodeId: string, stepLabel: string) => Promise<void>;
  onAbandonRun?: (runId: string, reason: string) => Promise<void>;
}

// One run, as a card an operator reads at a glance: whether it needs them, what it is called, and
// how far along it is. The progress is drawn as one mark per step rather than as a filled bar,
// because a workflow is a countable number of steps and "4 of 7" is the honest unit; a continuous
// bar invites reading a percentage into work that does not have one.
const WorkflowRunRow = memo<Props>(({ run, selected, onSelect, onRetryStep, onAbandonRun }) => {
  const [retrying, setRetrying] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const { busy, error, clearError, write } = useWorkflowWrite();
  // A run nothing can still be walking needs a person, because only an operator can end it. The
  // status stays `running`, which is what the record says; the chip is what reads differently.
  const chip = statusChip(run);
  const noSession = hasNoSessionBehindIt(run);
  const needsYou = run.status === 'awaiting_gate' || run.status === 'blocked' || noSession;
  // A run with nothing behind it is not live work, so its mark stops reading as in progress.
  const live = isRunActive(run.status) && !noSession;
  // A plan that no longer resolves has no honest step count, so it gets no marks at all rather
  // than a bar that would read as a finished run.
  const total = run.definition_missing ? 0 : run.nodes_total;

  // A run that ended says where it ended. The counts come from the definition, so a run that
  // outlived its version says only that it ended: its own cursor shown as a total would read as
  // a run that walked every step.
  const ended = run.status === 'completed' || run.status === 'abandoned';
  const endLabel = !ended
    ? null
    : run.definition_missing
      ? (run.status === 'completed' ? 'Completed' : 'Stopped')
      : run.status === 'completed'
        ? `Completed all ${run.nodes_total} steps`
        : `Stopped after ${run.nodes_done} of ${run.nodes_total} steps`;

  // A run that is still going says what it is on. The summary carries every eligible step, not
  // one of them, because a stage's members are unordered and choosing one would be a guess. This
  // sits last in the chain: a hold, a stranding or an ending all outrank it.
  const runningLabel = run.current_step_labels?.length
    ? `Working on ${run.current_step_labels.join(', ')}`
    : null;

  // Whether the run ended is asked before anything else. Ending a run leaves its failure record
  // open, so a chain that asks about the failure first makes a stopped run keep announcing the
  // step it was stuck on and never say that it stopped, which is the one fact this row owes the
  // operator reviewing it.
  //
  // The second question is whether anything is still walking it, which the screen knows only
  // from its own clock, so it says what it measured and leaves the conclusion to the reader.
  const holdLabel =
    endLabel ??
    (noSession
      ? 'Nothing has moved this in over five hours, longer than an agent session can live'
      : run.failed_on
        ? `Blocked on ${run.failed_on_label ?? run.failed_on}`
        : run.awaiting_gate_on
          ? (run.awaiting_gate_on_label ?? run.awaiting_gate_on)
          : run.definition_missing
            ? 'Workflow no longer available'
            : runningLabel);
  const holdColor = run.failed_on || run.definition_missing || noSession ? 'error.main' : 'warning.main';
  const chipColor = chip.color === 'default' ? 'primary' : chip.color;

  // A blocked run always needs a person, so the row carries the way out rather than hiding it
  // behind an expand. Retry only when the summary names exactly one failed step: the summary
  // reports one, and picking among several on the operator's behalf would be a guess, so a run
  // with more goes to the full view. Not offered on a stranded run either, where retrying would
  // show it running while every write tool stays refused and it could never move.
  const canRetry =
    Boolean(onRetryStep) &&
    run.status === 'blocked' &&
    Boolean(run.failed_on) &&
    !run.definition_missing;
  const active = isRunActive(run.status);
  const canAbandon = Boolean(onAbandonRun) && active;
  const failedLabel = run.failed_on_label ?? run.failed_on ?? '';

  // The row is a click target, so every control inside it stops the click reaching the card.
  const without = (go: () => void) => (e: React.SyntheticEvent) => {
    e.stopPropagation();
    go();
  };

  return (
    <Box
      onClick={() => onSelect(run.run_id)}
      aria-label={`${run.workflow_title}: ${chip.label}`}
      aria-current={selected}
      sx={{
        p: 1.25,
        mb: 0.75,
        cursor: 'pointer',
        border: 1,
        borderColor: selected ? 'text.primary' : 'divider',
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <Tooltip title={chip.label}>
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              flexShrink: 0,
              border: 1.5,
              borderColor: live || noSession ? `${chipColor}.main` : 'text.disabled',
              bgcolor: live ? `${chipColor}.main` : 'background.paper',
            }}
          />
        </Tooltip>
        <Typography variant="body2" noWrap sx={{ fontWeight: needsYou ? 600 : 500, minWidth: 0 }}>
          {run.workflow_title}
        </Typography>
        {/* The version is part of what the run is: two runs of the same workflow can have walked
            different procedures, and a run pins its version for life (WF-UC-11 step 2). */}
        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
          v{run.workflow_version}
        </Typography>
      </Stack>

      {/* What the run needs, said on the card. The drawing below says where it is; a card that
          only drew progress would make a run waiting on an approval look like one still working,
          and the operator would have to open each one to find the one holding them up. The state
          and the step are separate words because they answer separate questions: whether this
          run wants me, and which step it wants me at. */}
      {run.definition_missing && holdLabel !== 'Workflow no longer available' && (
        <Typography variant="caption" sx={{ display: 'block', pl: 2.25, color: 'error.main' }}>
          Workflow no longer available
        </Typography>
      )}

      {holdLabel && (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ pl: 2.25, mb: 0.5, minWidth: 0 }}
          alignItems="baseline"
          flexWrap="wrap"
          useFlexGap
        >
          {needsYou && (
            <Typography variant="caption" sx={{ color: holdColor, fontWeight: 600 }}>
              {chip.label}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 0 }}>
            {holdLabel}
          </Typography>
        </Stack>
      )}

      <Stack direction="row" spacing={0.5} sx={{ pl: 2.25 }} data-testid="run-progress">
        {Array.from({ length: total }, (_, i) => {
          const done = i < run.nodes_done;
          const here = i === run.nodes_done && live;
          return (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: 3,
                bgcolor: here
                  ? `${chipColor}.main`
                  : done
                    ? 'text.primary'
                    : 'divider',
              }}
            />
          );
        })}
      </Stack>

      {/* When it started and when it last moved, which is what an operator reads to spot a
          procedure active for days with no progress. The age is the fact being read, and the
          exact local time sits behind it for anyone who needs to quote one. */}
      <Tooltip
        title={`Started ${safeFormatDate(run.started_at)} · ${ended ? 'ended' : 'last moved'} ${safeFormatDate(run.updated_at)}`}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ pl: 2.25, mt: 0.5, color: 'text.secondary' }}
        >
          {`Started ${safeFormatDate(run.started_at, { dateStyle: 'medium', timeStyle: 'short' })} · ${ended ? 'ended' : 'last moved'} ${safeFormatAge(run.updated_at)}`}
        </Typography>
      </Tooltip>

      {error && (
        <Alert severity="error" sx={{ mt: 0.75 }} onClose={without(clearError)}>
          {error}
        </Alert>
      )}

      {(canRetry || canAbandon) && (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 0.75 }}>
          {canRetry && (
            <Button
              size="small"
              color="error"
              disabled={busy}
              onClick={without(() => setRetrying(true))}
            >
              Retry step
            </Button>
          )}
          {canAbandon && (
            <Button
              size="small"
              color="inherit"
              disabled={busy}
              onClick={without(() => setAbandoning(true))}
            >
              Abandon
            </Button>
          )}
        </Stack>
      )}

      {/* Both dialogs name the run. In the full view one run fills the screen and "this run" is
          unambiguous; launched from a list of rows it is not, and an abandon aimed at the wrong
          row cannot be taken back. */}
      <WorkflowRetryDialog
        open={retrying}
        runTitle={run.workflow_title}
        stepLabel={failedLabel}
        onCancel={() => setRetrying(false)}
        onConfirm={() => {
          setRetrying(false);
          const nodeId = run.failed_on;
          if (nodeId && onRetryStep) {
            void write(() => onRetryStep(run.run_id, nodeId, failedLabel));
          }
        }}
      />

      <WorkflowAbandonDialog
        open={abandoning}
        runTitle={run.workflow_title}
        onCancel={() => setAbandoning(false)}
        onConfirm={(reason) => {
          setAbandoning(false);
          if (onAbandonRun) void write(() => onAbandonRun(run.run_id, reason));
        }}
      />
    </Box>
  );
});

WorkflowRunRow.displayName = 'WorkflowRunRow';

export default WorkflowRunRow;
